import axios from 'axios'

import ApiConfig from '@/config/api'
import PdfBuilder from '@/pdf/PdfBuilder'
import { AudienceParams, DistribuicaoData, ReportApiResponse } from '@/pdf/types'

const REPORTS_API_BASE_URL =
    process.env.REACT_APP_API_URL?.toString()

function normalizeDistribuicao(
    rawItems: { label: string; quantity: number }[],
    insight?: string,
): DistribuicaoData {
    const total = rawItems.reduce((sum, i) => sum + i.quantity, 0)
    return {
        dados: rawItems.map(i => ({
            plataforma: i.label,
            origem: i.label,
            sessoes: i.quantity,
            percentual: total > 0 ? (i.quantity / total) * 100 : 0,
        })),
        insight,
    }
}

const normalizeReportData = (response: ReportApiResponse): AudienceParams => {
    const payload: any = response.data || response.report || response

    const companyName: string =
        payload.companyName ||
        response.companyName ||
        (() => {
            const titulo = payload.resumo_executivo?.titulo || response.resumo_executivo?.titulo || ''
            const partes = titulo.split('—')
            return partes.length > 1 ? partes[partes.length - 1].trim() : ''
        })() ||
        'Relatório de Audiência'

    return {
        companyName,
        logoBase64: payload.logoBase64 || response.logoBase64 || '',
        from:
            payload.from ||
            response.from ||
            payload.metadata?.periodo_atual?.inicio ||
            response.metadata?.periodo_atual?.inicio ||
            (() => { const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString() })(),
        to:
            payload.to ||
            response.to ||
            payload.metadata?.periodo_atual?.fim ||
            response.metadata?.periodo_atual?.fim ||
            new Date().toISOString(),
        program: payload.program || response.program || undefined,
        multipleRadiosMode: Boolean(payload.multipleRadiosMode || response.multipleRadiosMode),
        distribuicao_plataforma: (() => {
            const raw = payload.sessions_by_platform || response.sessions_by_platform
            if (raw) {
                const insight = (payload.distribuicao_plataforma as any)?.insight ||
                    (response.distribuicao_plataforma as any)?.insight
                return normalizeDistribuicao(
                    raw.map((i: any) => ({ label: i.platform, quantity: i.quantity })),
                    insight,
                )
            }
            return (payload.distribuicao_plataforma as DistribuicaoData) ||
                (response.distribuicao_plataforma as DistribuicaoData)
        })(),
        distribuicao_origem: (() => {
            const raw = payload.sessions_by_origin || response.sessions_by_origin
            if (raw) {
                const insight = (payload.distribuicao_origem as any)?.insight ||
                    (response.distribuicao_origem as any)?.insight
                return normalizeDistribuicao(
                    raw.map((i: any) => ({ label: i.origin, quantity: i.quantity })),
                    insight,
                )
            }
            return (payload.distribuicao_origem as DistribuicaoData) ||
                (response.distribuicao_origem as DistribuicaoData)
        })(),
        metricas_principais: (() => {
            const mp = payload.metricas_principais || response.metricas_principais
            if (mp) return mp

            // Novo formato plano da API
            const ts = payload.total_sessions || response.total_sessions
            const pa = payload.peak_audience || response.peak_audience
            const tl = payload.total_listened || response.total_listened
            const al = payload.average_listened || response.average_listened
            const aa = payload.average_audience || response.average_audience

            if (!ts && !pa && !tl && !al && !aa) return undefined

            const totalListenedMs = tl ? parseFloat(tl.current) : 0
            const totalHours = totalListenedMs / 3_600_000
            const totalHoursFormatted = `${Math.round(totalHours)}h`

            const avgListenedMs = al ? parseFloat(al.current) : 0
            const avgMinutes = avgListenedMs / 60_000
            const avgMinutesFormatted = `${avgMinutes.toFixed(2).replace('.', ',')} min`

            return {
                ...(ts ? { total_sessoes: { valor_atual: parseInt(ts.current, 10) } } : {}),
                ...(pa ? { pico_audiencia: { valor_atual: pa.current.value } } : {}),
                ...(aa ? { media_ouvintes_simultaneos: { valor_atual: parseInt(aa.current, 10) } } : {}),
                ...(tl ? { tempo_total_audicao: { valor_atual: Math.round(totalHours), valor_atual_horas: totalHours, valor_atual_formatado: totalHoursFormatted } } : {}),
                ...(al ? { tempo_medio_por_sessao: { valor_atual: avgMinutes, valor_atual_minutos: avgMinutes, valor_atual_formatado: avgMinutesFormatted } } : {}),
            }
        })(),
        resumo_executivo:
            payload.resumo_executivo || response.resumo_executivo,
        durations_data:
            payload.durations_data || response.durations_data || undefined,
        distribuicao_duracao:
            payload.distribuicao_duracao || response.distribuicao_duracao,
        audience_data:
            payload.audience_data || response.audience_data || [],
        audience_insight:
            payload.audience_insight || response.audience_insight || response.resumo_executivo?.frase_destaque || '',
        comportamento_audiencia:
            payload.comportamento_audiencia || response.comportamento_audiencia,
        insight_unificado_metricas_principais:
            payload.insight_unificado_metricas_principais || response.insight_unificado_metricas_principais,
        insight_geral: (() => {
            const ig = payload.insight_geral || response.insight_geral
            if (!ig) return undefined
            if (typeof ig === 'string') return ig
            if (typeof ig === 'object') return (ig as any).sintese || (ig as any).insight || (ig as any).text || (ig as any).value || undefined
            return String(ig)
        })(),
    }
}

export default class PdfService {
    // 1. O método principal agora repassa os params para o fetchReportData
    static async generateCoverAndOpen(companyUuid: string, params: { from: string; to: string }): Promise<void> {
        // Passamos os params (datas) aqui 
        const reportData = await this.fetchReportData(companyUuid, params)

        // Garantir que a capa exiba o intervalo de datas selecionado pelo usuário,
        // mesmo que a API retorne outro intervalo.
        reportData.from = params.from
        reportData.to = params.to

        const pdfBytes = await PdfBuilder.buildCoverPdf(reportData)
        const blob = new Blob([pdfBytes], { type: 'application/pdf' })
        const pdfNumber = Date.now()

        const radioName = (reportData.companyName || '').replace(/\s+/g, ' ').trim()
        
        function formatDate(dateStr?: string) {
            if (!dateStr) return ''
            const d = new Date(dateStr)
            if (isNaN(d.getTime())) return ''
            return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
        }

        const from = formatDate(reportData.from)
        const to = formatDate(reportData.to)
        let periodo = (from && to) ? `(${from} - ${to})` : from ? `(${from})` : to ? `(${to})` : ''

        let fileName = `Análise de Audiência${radioName ? ` - ${radioName}` : ''}${periodo ? ` ${periodo}` : ''} - ${pdfNumber}.pdf`

        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = fileName
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)

        window.setTimeout(() => URL.revokeObjectURL(url), 100000)
    }
    
    private static async fetchReportData(
        companyUuid: string,
        dates: { from: string; to: string } 
    ): Promise<AudienceParams> {
        const token = localStorage.getItem('token')
        const authHeaders = token ? { Authorization: `Bearer ${token}` } : undefined

        try {
            const [reportResponse, companyResponse] = await Promise.allSettled([
                axios.get(
                    `${REPORTS_API_BASE_URL}/reports/${companyUuid}`,
                    { 
                        timeout: 0, 
                        headers: authHeaders,
                        params: {
                            from: dates.from,
                            to: dates.to,
                            startTime: '00:00',
                            endTime: '23:59',
                            weekdays: '',
                            program: ''
                        }
                    }
                ) as Promise<{ data: ReportApiResponse }>,
                axios.get(
                    `${ApiConfig.apiUrl}/company/${companyUuid}`,
                    { headers: authHeaders },
                ),
            ])

            if (reportResponse.status === 'rejected') {
                const error: any = reportResponse.reason;
                if (error?.response?.status === 404 || error?.response?.data?.message === 'Não há dados para o período selecionado') {
                    throw new Error('Não há dados para o período selecionado');
                }
                if (error?.code === 'ECONNABORTED') {
                    throw new Error('A geração do relatório demorou mais que o esperado.');
                }
                throw new Error(error?.response?.data?.message || 'Erro ao buscar dados do relatório.');
            }
            
            const params = normalizeReportData(reportResponse.value.data)

            if (companyResponse.status === 'fulfilled') {
                const company = companyResponse.value.data
                if (company?.name) params.companyName = company.name
                if (company?.logoBase64) params.logoBase64 = company.logoBase64
            }

            return params
        } catch (error: any) {
            if (error?.code === 'ECONNABORTED') {
                throw new Error('Tempo esgotado ao gerar o relatório.')
            }
            throw error
        }
    }
}